<template>
  <div id="crew-setting-navigation">
    <div class="flex w-full justify-end items-center">
      <div class="flex items-center" v-if="isTrial">
        <!-- トライアル中の場合表示 -->
        <div class="mr-4">
          <vs-tooltip :text="trialTooltipText" position="bottom">
            <div class="flex justify-end items-center">
              <div class="flex justify-center items-center mr-1">
                <span
                  class="material-icons-outlined"
                  :style="{ color: AISEA_COLOR.BLUE, 'font-size': '18px' }"
                >
                  check_circle
                </span>
              </div>
              <div>無料トライアル中</div>
            </div>
          </vs-tooltip>
        </div>

        <div class="button-inner">
          <div
            class="
              w-full
              mr-4
              p-4
              flex
              justify-center
              items-center
              cursor-pointer
            "
            style="border-radius: 6px; height: 24px; border: 1px solid"
            :style="{
              'background-color': AISEA_COLOR.PURE_WHITE,
              'border-color': AISEA_COLOR.BLUE,
            }"
            @click="showUserPlanDialog()"
          >
            <div
              :style="{
                width: '72px',
                color: AISEA_COLOR.BLUE,
                'font-size': '14px',
              }"
            >
              プラン変更
            </div>
          </div>
        </div>
      </div>

      <div class="mr-4">
        <vs-tooltip :text="guideTooltip" position="left">
          <div class="flex justify-end items-center" @click="onShowOnboarding">
            <div class="flex justify-center items-center mr-1">
              <span
                class="material-icons"
                :style="{ color: AISEA_COLOR.BLUE, 'font-size': '20px' }"
              >
                info
              </span>
            </div>
            <div>利用ガイド</div>
          </div>
        </vs-tooltip>
      </div>

      <div
        class="flex justify-end items-center"
        :style="{
          'min-width': isCrewDashboardNavigationHidden ? '176px' : '364px',
        }"
      >
        <div
          class="button-inner mr-2"
          v-show="!isCrewDashboardNavigationHidden"
        >
          <div
            class="w-full h-12 p-6 flex justify-center items-center cursor-pointer"
            style="border-radius: 6px; height: 44px; margin-right: 12px;"
            :style="{ 'background-color': AISEA_COLOR.BLUE }"
            @click="linkTo('enterprise-management-crew-status-list')"
          >
            <span
              class="material-icons-outlined"
              :style="{ color: AISEA_COLOR.WHITE, 'font-size': '16px' }"
            >
              fact_check
            </span>
            <div class="ml-2 aisea-text-16-400-white">ダッシュボード</div>
          </div>
        </div>
        <navigation-select
          v-if="dailyCrewListOptions.length > 0"
          class="mr-2"
          menuButtonLabel="日次船員一覧"
          :menuItemOptions="dailyCrewListOptions"
          @onClickItem="item => openCrewVesselApp(item)"
        >
          <template slot="icon">
            <span
              class="material-symbols-outlined mr-2"
              :style="{ color: AISEA_COLOR.WHITE, 'font-size': '16px' }"
            >
              overview
            </span>
          </template>
        </navigation-select>

        <navigation-select
          menuButtonLabel="労務管理設定"
          :menuItemOptions="filteredSettingsItemOptions"
          @onClickItem="item => linkTo(item.path)"
        >
          <template slot="icon">
            <feather-icon
              class="settings-icon"
              icon="SettingsIcon"
              svgClasses="h-4 w-4 mr-2"
          /></template>
        </navigation-select>
      </div>
    </div>

    <!-- オンボーディングダイアログ -->
    <vs-prompt
      title=""
      :color="AISEA_COLOR.DARK_GRAY"
      buttons-hidden
      :active.sync="showOnboarding"
      class="on-boarding-dialog"
    >
      <onboarding-contents-frame />
    </vs-prompt>

    <!-- プラン変更ダイアログ -->
    <user-plan-dialog ref="UserPlanDialog" />
  </div>
</template>

<script>
import authService from "@/services/authService";
import { DeviceInfoMixin, ColorMixin } from "@/mixins";
import OnboardingContentsFrame from "../../onboarding/OnboardingContentsFrame";
import UserPlanDialog from "./UserPlanDialog";
import { createNamespacedHelpers, mapGetters } from "vuex";
import NavigationSelect from "../components/NavigationSelect.vue";
const { mapActions, mapState } = createNamespacedHelpers("crewTier");
import { AiseaApiFactory } from "@/api/aisea/apiFactory";

const enterpriseManningVesselRepository = AiseaApiFactory.get(
  "enterpriseManningVessel"
);
const accountRepository = AiseaApiFactory.get("account");

export default {
  components: {
    OnboardingContentsFrame,
    UserPlanDialog,
    NavigationSelect,
  },
  mixins: [DeviceInfoMixin, ColorMixin],
  data() {
    return {
      open: false,
      iconPathMap: {
        crewLaborIcon: require(`@/assets/images/icons/crew/crew_labor.svg`),
        vesselDetailIcon: require(`@/assets/images/icons/crew/vessel_detail.svg`),
        seamanIcon: require(`@/assets/images/icons/crew/seaman.svg`),
        adminIcon: require(`@/assets/images/icons/crew/admin.svg`),
      },
      iconSvgMap: {},
      guideTooltip: "Aisea Crewの利用方法を こちらからご確認ください。",
      showOnboarding: false,
      activeTabOnboarding: 0,
      showTrialButton: null,
      breadcrumbRightPosition: 0,
      crewSettingNavigationBlockHeight: 0,
      dailyCrewListOptions: [],
      settingsItemOptions: [
        {
          name: "労務基本情報",
          path: "enterprise-management-crew-labor-template",
        },
        {
          name: "船舶情報",
          path: "enterprise-management-crew-vessels-list",
        },
        {
          name: "船員一覧",
          path: "enterprise-management-crew-list",
        },
        {
          name: "陸上従業員一覧",
          path: "enterprise-management-crew-admin-account-list",
        },
        {
          name: "乗船期間一覧",
          path: "enterprise-management-corp-manning-vessels-list",
        },
        {
          name: "作業種別管理",
          path: "enterprise-management-task-kind-list",
        },
        {
          name: "データ管理",
          path: "enterprise-management-data-management",
          addonId: authService.ADDON_IDS.CREW_PAYROLL,
        },
      ],
    };
  },
  computed: {
    ...mapState(["expiredTier", "isTrial"]),
    ...mapGetters(["addonMap"]),
    trialTooltipText() {
      return `${this.expiredTier}まで`;
    },
    isCrewDashboardNavigationHidden() {
      return this.$route.meta.isCrewDashboardNavigationHidden;
    },
    routeName() {
      return this.$route.name;
    },
    filteredSettingsItemOptions() {
      const { addonMap, settingsItemOptions } = this;
      const options = settingsItemOptions.filter(item => {
        if (!Object.prototype.hasOwnProperty.call(item, "addonId")) {
          return true;
        }
        const { addonId } = item;
        return !!addonMap[addonId];
      });
      return options;
    },
  },
  watch: {
    routeName() {
      this.setBreadcrumbRightPosition();
    },
    onChangeWindowSize() {
      this.setBreadcrumbRightPosition();
    },
    "$store.state.verticalNavMenuWidth"() {
      setTimeout(() => {
        this.setBreadcrumbRightPosition();
      }, 400);
    },
  },
  methods: {
    ...mapActions(["fetchAddon"]),
    linkTo(path, query) {
      this.$router.push({ name: path, query: query }, () => {});
      this.open = false;
      this.$nextTick(() => {
        this.setBreadcrumbRightPosition();
      });
    },
    async openCrewVesselApp(optionItem) {
      const enterpriseVesselId = optionItem.enterpriseVesselId;
      const accountId = optionItem.accountId;

      const tokenRes = await accountRepository.getSsoToken(
        accountId,
        enterpriseVesselId
      );
      if (tokenRes.status) {
        const token = tokenRes.data.token;
        window.open(
          `${process.env.VUE_APP_CREW_VESSEL_URL}?token=${token}`,
          "_blank"
        );
      }
    },
    isSelectedTab(_item) {
      return false;
      // if (!item.query ) {
      //   return false;
      // }
      // return String(this.$route.query.tab) === String(item.query.tab);
    },
    async fetchDailyCrewListOptions() {
      const res = await enterpriseManningVesselRepository.search({
        is_for_crew_app: true,
        size: 100,
      });
      if (res.status) {
        res.data.manning_vessels.forEach(manningVessel => {
          if (manningVessel.accounts.length > 0) {
            this.dailyCrewListOptions.push({
              accountId: manningVessel.accounts[0].id,
              enterpriseVesselId: manningVessel.id,
              name: manningVessel.name,
            });
          }
        });
      }
    },
    async fetchToken() {
      await accountRepository.getSsoToken();
    },

    fetchSvgIcons() {
      ["crewLaborIcon", "vesselDetailIcon", "seamanIcon", "adminIcon"].forEach(
        async itemIcon => {
          const svgText = await fetch(this.iconPathMap[itemIcon]).then(
            response => {
              return response.text();
            }
          );
          const divElement = document.getElementById(
            `menu-item-icon-${itemIcon}`
          );
          if (divElement) {
            divElement.innerHTML = svgText
              .replace(/fill="[^"]+"/g, 'fill=""')
              .replace(/(mask\d*)_/g, "$1_1");
          }
        }
      );
    },
    onShowOnboarding() {
      this.showOnboarding = true;
    },
    showUserPlanDialog() {
      this.$refs.UserPlanDialog.showPlanDialog = true;
    },

    setBreadcrumbRightPosition() {
      const ulElement = document.querySelector("div.vx-breadcrumb ul");
      if (ulElement) {
        this.breadcrumbRightPosition = Math.floor(
          ulElement.getBoundingClientRect().right
        );
        this.$nextTick(() => {
          const navigationBLockEl = document.getElementById(
            "crew-setting-navigation-block"
          );
          if (navigationBLockEl) {
            const crewSettingNavigationBlockHeight = Math.floor(
              navigationBLockEl.getBoundingClientRect().height
            );
            this.crewSettingNavigationBlockHeight = crewSettingNavigationBlockHeight;
            let routerHeaderClass = document.getElementsByClassName(
              "router-header"
            );
            if (crewSettingNavigationBlockHeight > 60) {
              routerHeaderClass[0].style.marginBottom = "56px";
            } else {
              routerHeaderClass[0].style.marginBottom = "21px";
            }
          }
        });
      }
    },
  },
  mounted() {
    this.setBreadcrumbRightPosition();
  },
  async created() {
    this.fetchSvgIcons();

    authService.getUser().then(user => {
      if (user.is_first_login === true) {
        // 「初回ログイン」のフラグを検知したら、オンボーディングのダイアログを自動起動
        this.showOnboarding = true;
        user.is_first_login = false; // フラグは戻しておく
        authService.saveUser(user);
      }
    });

    await this.fetchAddon();

    await this.fetchDailyCrewListOptions();
  },
};
</script>

<style lang="scss">
@import "src/assets/scss/aisea/variables";
@import url("https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,0,0");

#crew-setting-navigation {
  position: relative;
  width: fit-content;
  margin-left: auto;
}

.material-symbols-outlined {
  font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24;
}

.on-boarding-dialog {
  .vs-dialog {
    min-height: calc(100vh - 40px - 40px) !important;
    max-width: 80vw;
    background-color: $aisea-white !important;

    .vs-dialog-header {
      background-color: $aisea-white !important;
    }

    .vs-dialog-text {
      padding: 0 !important;
    }

    .dialog-title {
      font-family: $aisea-font-family !important;
      font-size: 18px;
    }

    .vs-dialog-cancel {
      background-color: rgba(244, 244, 244, 0) !important;
      transform: none !important;
      box-shadow: none !important;
    }
  }
}
</style>
